import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Icon } from "@/components/landing/Icon";
import { Reveal, staggerContainer, staggerItem } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { CATEGORY_META } from "@/lib/taxation";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

const CARD_META = [
  { key: "GST Services", parentSlug: "gst", icon: "Receipt", accentText: "text-emerald2-600" },
  { key: "Income Tax", parentSlug: "income-tax", icon: "Landmark", accentText: "text-gold-700" },
];

export default function Taxation() {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    api
      .listServices()
      .then(({ services }) => {
        const next = {};
        (services || []).forEach((s) => {
          next[s.category] = (next[s.category] || 0) + 1;
        });
        setCounts(next);
      })
      .catch(() => {});
  }, []);

  const cards = CARD_META.map((c) => ({ ...c, count: counts[c.key] ?? "—" }));

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="taxation-page">
      <SEO
        title="GST & Income Tax Advisory & Filing Services | Biz360 Compliance"
        description="Comprehensive GST registration, return filing, refunds, LUT, and Direct Income Tax planning & assessment representation for enterprises in Maharashtra & India."
        canonical="/taxation"
        keywords="GST registration India, GST return filing Pune, Income Tax filing Maharashtra, TDS compliance, GST refund, tax notices, direct tax advisory"
        breadcrumbs={[{ label: "Taxation", href: "/taxation" }]}
      />
      <ScrollProgress />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-16 md:pt-40 md:pb-20">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <div className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="taxation-breadcrumb">
                <Link to="/" className="hover:text-navy-800">Home</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-navy-700">Taxation</span>
              </div>
            </Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <Reveal>
                <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
                  Taxation
                </span>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-4 text-3xl font-bold tracking-tight text-navy-900 md:text-5xl">GST and Income Tax, handled end-to-end</h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-4 text-base text-slate-600 md:text-lg">
                  From registration and returns to notices and appeals — pick a track below to see eligibility, documents, process and fees for every service.
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="relative bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: false, margin: "-60px" }}
              className="grid grid-cols-1 gap-8 md:grid-cols-2"
            >
              {cards.map((c) => {
                const meta = CATEGORY_META[c.key];
                return (
                  <motion.div key={c.key} variants={staggerItem}>
                    <Link
                      to={`/taxation/${c.parentSlug}`}
                      data-testid={`taxation-card-${c.parentSlug}`}
                      className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-2 hover:shadow-[0_24px_50px_rgba(15,30,60,0.12)]"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img src={meta.image} alt={c.key} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-navy-950/10 to-transparent" />
                        <span className={`absolute left-5 top-5 grid h-11 w-11 place-items-center rounded-2xl bg-white/95 ${c.accentText} shadow-[0_6px_16px_rgba(15,30,60,0.18)]`}>
                          <Icon name={c.icon} className="h-5 w-5" />
                        </span>
                        <span className="absolute bottom-4 left-5 text-xs font-bold uppercase tracking-widest text-white/80">
                          {c.count} services
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-7">
                        <h2 className="text-xl font-bold text-navy-900 group-hover:text-navy-700">{c.key}</h2>
                        <p className="mt-2 text-sm leading-relaxed text-slate-600">{meta.tagline}</p>
                        <span className={`mt-auto flex items-center gap-1.5 pt-6 text-sm font-semibold ${c.accentText}`}>
                          Explore {c.key} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
